import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { MapPin, TrendingUp, Cpu, Database, Filter, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InteractiveMapSection = () => {
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [mapData, setMapData] = useState({ districts: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDistrictsData();
  }, []);

  const fetchDistrictsData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/districts`);
      setMapData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching districts data:', error);
      setLoading(false);
    }
  };

  const getDistrictColor = (district) => {
    if (district.nutritionScore >= 90) return "bg-green-500";
    if (district.nutritionScore >= 85) return "bg-blue-500";  
    if (district.nutritionScore >= 80) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getDistrictPosition = (district) => {
    // Convert lat/lng to relative positioning within Gujarat bounds
    const gujaratLatBounds = { min: 20.1, max: 24.7 }; // Gujarat approximate bounds
    const gujaratLngBounds = { min: 68.2, max: 74.5 }; // Gujarat approximate bounds
    
    const x = ((district.lng - gujaratLngBounds.min) / (gujaratLngBounds.max - gujaratLngBounds.min)) * 100;
    const y = ((gujaratLatBounds.max - district.lat) / (gujaratLatBounds.max - gujaratLatBounds.min)) * 100;
    
    return { x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) };
  };

  const filteredDistricts = mapData.districts.filter(district => {
    // Filter by performance
    let performanceMatch = true;
    if (filterType === "high") performanceMatch = district.nutritionScore >= 90;
    else if (filterType === "medium") performanceMatch = district.nutritionScore >= 80 && district.nutritionScore < 90;
    else if (filterType === "low") performanceMatch = district.nutritionScore < 80;
    
    // Filter by region
    let regionMatch = regionFilter === "all" || district.region === regionFilter;
    
    return performanceMatch && regionMatch;
  });

  const regions = ["all", ...new Set(mapData.districts.map(d => d.region))];

  if (loading) {
    return (
      <section id="map" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-pink-500" />
            <p className="text-gray-600">Loading Gujarat WCD map data...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="map" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-6">
            <MapPin className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Interactive Gujarat WCD Map
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real-time visualization of women & child development programs across all Gujarat districts
          </p>
        </div>

        {/* Filters */}
        <div className="mb-12">
          {/* Performance Filters */}
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <Button 
              variant={filterType === "all" ? "default" : "outline"}
              onClick={() => setFilterType("all")}
              className={filterType === "all" ? "bg-pink-500 hover:bg-pink-600" : ""}
            >
              <Filter className="w-4 h-4 mr-2" />
              All Districts
            </Button>
            <Button 
              variant={filterType === "high" ? "default" : "outline"}
              onClick={() => setFilterType("high")}
              className={filterType === "high" ? "bg-green-500 hover:bg-green-600" : ""}
            >
              High Performance (90%+)
            </Button>
            <Button 
              variant={filterType === "medium" ? "default" : "outline"}
              onClick={() => setFilterType("medium")}
              className={filterType === "medium" ? "bg-blue-500 hover:bg-blue-600" : ""}
            >
              Medium Performance (80-90%)
            </Button>
            <Button 
              variant={filterType === "low" ? "default" : "outline"}
              onClick={() => setFilterType("low")}
              className={filterType === "low" ? "bg-yellow-500 hover:bg-yellow-600" : ""}
            >
              Developing (&lt;80%)
            </Button>
          </div>
          
          {/* Region Filters */}
          <div className="flex flex-wrap justify-center gap-3">
            {regions.map(region => (
              <Button 
                key={region}
                variant={regionFilter === region ? "default" : "outline"}
                size="sm"
                onClick={() => setRegionFilter(region)}
                className={regionFilter === region ? "bg-pink-500 hover:bg-pink-600 text-xs" : "text-xs"}
              >
                {region === "all" ? "All Regions" : region}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map Visualization */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-pink-500" />
                  WCD Performance Map
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Simulated Map Area */}
                <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-8 h-96 overflow-hidden border-2 border-blue-200">
                  <div className="absolute inset-0 opacity-30">
                    <svg viewBox="0 0 500 400" className="w-full h-full">
                      {/* Gujarat outline - distinctive coastline and border shape */}
                      <path
                        d="M50 180 L70 160 Q90 140 120 135 Q150 130 180 125 Q220 120 260 125 Q300 130 340 140 Q380 150 420 160 L430 180 Q440 200 435 230 L420 260 Q400 280 380 290 L350 300 Q320 310 290 315 L250 320 Q200 315 150 300 Q100 280 70 250 Q50 220 50 200 Z"
                        fill="currentColor"
                        className="text-pink-200 stroke-pink-300"
                        strokeWidth="2"
                      />
                      {/* Add Gujarat geographical features */}
                      <circle cx="380" cy="180" r="4" fill="currentColor" className="text-blue-400" opacity="0.6" /> {/* Kutch region */}
                      <circle cx="200" cy="280" r="3" fill="currentColor" className="text-blue-400" opacity="0.6" /> {/* Gulf of Khambhat */}
                      <circle cx="150" cy="250" r="2" fill="currentColor" className="text-blue-400" opacity="0.6" /> {/* Coastal areas */}
                      <circle cx="320" cy="200" r="2" fill="currentColor" className="text-blue-400" opacity="0.6" /> {/* Rivers */}
                    </svg>
                  </div>
                  
                  {/* District Markers */}
                  <div className="absolute inset-0">
                    {filteredDistricts.map((district) => {
                      const position = getDistrictPosition(district);
                      return (
                        <div
                          key={district.name}
                          className={`absolute w-4 h-4 rounded-full ${getDistrictColor(district)} cursor-pointer transform hover:scale-150 transition-all duration-300 shadow-lg animate-pulse border-2 border-white`}
                          style={{
                            left: `${position.x}%`,
                            top: `${position.y}%`
                          }}
                          onClick={() => setSelectedDistrict(district)}
                          title={`${district.name} - Nutrition Score: ${district.nutritionScore}%`}
                        />
                      );
                    })}
                  </div>
                  
                  {/* Legend */}
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                    <h4 className="font-semibold text-sm mb-2">Nutrition Score</h4>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-2" />
                        <span>90%+ Excellent</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-2" />
                        <span>85-90% Good</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2" />
                        <span>80-85% Average</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-red-500 rounded-full mr-2" />
                        <span>&lt;80% Needs Focus</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* District Details */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="w-5 h-5 mr-2 text-pink-500" />
                  District WCD Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDistrict ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {selectedDistrict.name}
                      </h3>
                      <div className="flex gap-2 mb-3">
                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                          {selectedDistrict.region}
                        </Badge>
                        <Badge className={`text-xs ${
                          selectedDistrict.nutritionScore >= 90 ? 'bg-green-100 text-green-700' :
                          selectedDistrict.nutritionScore >= 85 ? 'bg-blue-100 text-blue-700' :
                          selectedDistrict.nutritionScore >= 80 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          Nutrition Score: {selectedDistrict.nutritionScore}%
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Anganwadi Centers</span>
                        <span className="font-semibold text-lg">{selectedDistrict.anganwadiCenters.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Nutrition Score</span>
                        <span className="font-semibold text-lg">{selectedDistrict.nutritionScore}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Geographic Region</span>
                        <span className="font-semibold text-sm">{selectedDistrict.region}</span>
                      </div>
                      
                      {/* Show real data if available (Dahod district) */}
                      {selectedDistrict.realData && (
                        <div className="pt-3 border-t">
                          <h4 className="font-semibold text-sm text-pink-600 mb-2">Real-Time WCD Data</h4>
                          <div className="space-y-2 text-xs">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Children Monitored</span>
                              <span className="font-semibold">{selectedDistrict.realData.childrenMonitored.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Coverage Rate</span>
                              <span className="font-semibold">{selectedDistrict.realData.coverageRate}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Fortified Rice</span>
                              <span className="font-semibold">{selectedDistrict.realData.fortifiedRice}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Stunting Rate</span>
                              <span className="font-semibold">{selectedDistrict.realData.stunting}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Sectors</span>
                              <span className="font-semibold">{selectedDistrict.realData.sectors}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="pt-4 border-t">
                      <Button size="sm" className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700">
                        View WCD Report
                        <TrendingUp className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Cpu className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      Click on any district marker to view detailed WCD insights and Anganwadi center statistics
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">State Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Districts</span>
                  <span className="font-semibold">{mapData.districts.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Avg Digital Score</span>
                  <span className="font-semibold">
                    {Math.round(mapData.districts.reduce((acc, d) => acc + d.digitalScore, 0) / mapData.districts.length)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total AI Projects</span>
                  <span className="font-semibold">
                    {mapData.districts.reduce((acc, d) => acc + d.aiProjects, 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveMapSection;